
import { Product } from "@/lib/types";
import { generateId, addUsageRecord, addStockUpdate } from "@/lib/storage";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export const useProductActions = () => {
  const { toast } = useToast();
  const [actionInProgress, setActionInProgress] = useState(false);

  const handleAddStock = (
    product: Product,
    setSelectedProduct: (product: Product | null) => void,
    setStockUpdateDialogOpen: (open: boolean) => void
  ) => {
    console.log("Opening add stock dialog for:", product.name);
    setSelectedProduct(product);
    setStockUpdateDialogOpen(true);
  };

  const handleRecordUsage = (
    product: Product,
    setSelectedProduct: (product: Product | null) => void,
    setUsageDialogOpen: (open: boolean) => void
  ) => {
    console.log("Opening record usage dialog for:", product.name);
    setSelectedProduct(product);
    setUsageDialogOpen(true);
  };

  const handleRecordPastUsage = (
    product: Product,
    setSelectedProduct: (product: Product | null) => void,
    setRetrospectiveUsageDialogOpen: (open: boolean) => void
  ) => {
    console.log("Opening retrospective usage dialog for:", product.name);
    setSelectedProduct(product);
    setRetrospectiveUsageDialogOpen(true);
  };

  const handleQuickUsage = async (product: Product, refreshData: () => void) => {
    if (actionInProgress) {
      console.log("Action already in progress, ignoring request");
      return;
    }

    try {
      console.log("Recording quick usage (1 unit) for:", product.name);
      setActionInProgress(true);
      
      const usageRecord = {
        id: generateId(),
        productId: product.id,
        quantity: 1,
        date: new Date(),
      };
      
      await addUsageRecord(usageRecord);
      
      toast({
        title: "Usage Recorded",
        description: `Used 1 unit of ${product.name}`,
      });
      
      refreshData();
    } catch (error) {
      console.error("Error recording quick usage:", error);
      toast({
        title: "Error",
        description: "Failed to record usage",
        variant: "destructive",
      });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteProduct = async (
    productId: string,
    products: Product[],
    refreshData: () => void
  ) => {
    try {
      console.log("Deleting product:", productId);
      // Use the dynamic import to avoid circular dependencies
      const storage = await import("@/lib/storage");
      
      await storage.deleteProduct(productId);
      
      const productName = products.find(p => p.id === productId)?.name || "Product";
      
      toast({
        title: "Product Deleted",
        description: `${productName} has been removed`,
      });
      
      refreshData();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive",
      });
    }
  };

  return {
    handleAddStock,
    handleRecordUsage,
    handleRecordPastUsage,
    handleQuickUsage,
    handleDeleteProduct
  };
};
